import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Stack,
  Paper,
  IconButton,
  Avatar,
  AvatarGroup,
  useTheme,
  useMediaQuery,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  SportsEsports as GameIcon,
  EmojiEvents as TrophyIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Casino as CasinoIcon,
  TrendingUp as RemontadaIcon,
  LocalFireDepartment as PalizaIcon,
  Extension as ComboIcon,
  Shield as ResilienciaIcon,
  Person as SobrevivienteIcon,
  Bolt as RapidaIcon
} from '@mui/icons-material';
import api from '../services/api';
import { debounce } from 'lodash';
import RaceChip from '../components/RaceChip';
import BottomNav from '../components/BottomNav';
import SectionTitle from '../components/SectionTitle';
import CustomPlayerInput from '../components/CustomPlayerInput';

// NumberInputWithLabel para el contador de Partidas
function NumberInputWithLabel({ label, value, onChange, max = null, min = 0, width = '100%' }) {
  const handleIncrement = () => {
    const currentValue = parseInt(value) || 0;
    if (max === null || currentValue < max) {
      onChange((currentValue + 1).toString());
    }
  };

  const handleDecrement = () => {
    const currentValue = parseInt(value) || 0;
    if (currentValue > min) {
      onChange((currentValue - 1).toString());
    }
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      width: width,
      height: '64px',
    }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Paper
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          width: '100%',
          height: '43px',
          transition: 'none',
          '& *': {
            transition: 'none !important'
          }
        }}
      >
        <IconButton
          size="small"
          onClick={handleDecrement}
          disabled={parseInt(value) <= min}
          sx={{
            width: 30,
            height: 30,
            bgcolor: 'primary.main',
            color: 'rgba(0, 0, 0, 0.6)',
            mt: '-2px',
            '&:hover': { 
              bgcolor: 'primary.dark'
            },
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled'
            }
          }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>

        <Typography 
          sx={{ 
            minWidth: 32,
            textAlign: 'center',
            userSelect: 'none',
            fontWeight: 'medium',
            mt: '2px'
          }}
        >
          {value}
        </Typography>

        <IconButton
          size="small"
          onClick={handleIncrement}
          disabled={max !== null && parseInt(value) >= max}
          sx={{
            width: 30,
            height: 30,
            bgcolor: 'primary.main',
            color: 'rgba(0, 0, 0, 0.6)',
            mt: '-2px',
            '&:hover': { 
              bgcolor: 'primary.dark'
            },
            '&.Mui-disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled'
            }
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Paper>
    </Box>
  );
}

// NumberInput simple para los contadores de Victoria
const NumberInput = React.memo(({ value, onChange, width }) => {
  const handleClick = (increment) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const currentValue = parseInt(value) || 0;
    const newValue = increment ? currentValue + 1 : currentValue - 1;
    if (newValue >= 0) {
      onChange(newValue);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: width || '100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        p: 0.5,
        position: 'relative',
        zIndex: 1,
        userSelect: 'none'
      }}
    >
      <IconButton
        size="small"
        onClick={handleClick(false)}
        disabled={parseInt(value) <= 0}
        sx={{
          width: 30,
          height: 30,
          bgcolor: 'primary.main',
          color: 'rgba(0, 0, 0, 0.6)',
          '&:hover': { 
            bgcolor: 'primary.dark'
          },
          '&.Mui-disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled'
          }
        }}
      >
        <RemoveIcon fontSize="small" />
      </IconButton>

      <Typography 
        sx={{ 
          minWidth: 32,
          textAlign: 'center',
          userSelect: 'none',
          fontWeight: 'medium'
        }}
      >
        {value}
      </Typography>

      <IconButton
        size="small"
        onClick={handleClick(true)}
        sx={{
          width: 30,
          height: 30,
          bgcolor: 'primary.main',
          color: 'rgba(0, 0, 0, 0.6)',
          '&:hover': { 
            bgcolor: 'primary.dark'
          }
        }}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Paper>
  );
}, (prevProps, nextProps) => {
  return prevProps.value === nextProps.value;
});

// Modificar el PlayerSearchInput1
const PlayerSearchInput1 = React.memo(({ onSearch, value, disabled, setActiveInput }) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        width: '100%',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <SearchIcon sx={{ color: disabled ? 'text.disabled' : 'primary.main' }} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => {
          e.preventDefault();
          onSearch(e.target.value);
        }}
        onFocus={(e) => {
          e.preventDefault();
          setActiveInput(1);
        }}
        placeholder="Buscar jugador..."
        disabled={disabled}
        style={{
          width: '100%',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: disabled ? 'inherit' : 'white',
          fontSize: '16px',
          padding: '4px 0',
          WebkitAppearance: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    </Box>
  );
});

// Modificar el PlayerSearchInput2
const PlayerSearchInput2 = React.memo(({ onSearch, value, disabled, setActiveInput }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [disabled]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        width: '100%',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto'
      }}
    >
      <SearchIcon sx={{ color: disabled ? 'text.disabled' : 'primary.main' }} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onSearch(e.target.value)}
        onFocus={(e) => {
          setActiveInput(2);
          e.preventDefault();
        }}
        placeholder="Buscar jugador..."
        disabled={disabled}
        style={{
          width: '100%',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: disabled ? 'inherit' : 'white',
          fontSize: '16px',
          padding: '4px 0',
          WebkitAppearance: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    </Box>
  );
});

// Optimizar el DeckSelector
const DeckSelector = React.memo(({ player, teamNumber, selectedDeck, setTeam1SelectedDecks, setTeam2SelectedDecks }) => {
  console.log('DeckSelector render:', {
    playerId: player?.id,
    teamNumber,
    selectedDeckId: selectedDeck?.deckId,
    timestamp: new Date().getTime()
  });

  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const decksLoadedRef = useRef(false);

  const loadDecks = useCallback(async () => {
    if (!player?.id || decksLoadedRef.current) {
      console.log('Skipping deck load:', { playerId: player?.id, alreadyLoaded: decksLoadedRef.current });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/decks/user/${player.id}`, {
        withCredentials: true
      });
      console.log('Decks loaded for player:', player.id, response.data);
    
      // Filtrar y ordenar los mazos
      const filteredDecks = response.data.filter(deck => {
        // Excluir cualquier mazo que tenga "Default" en el nombre o sea un mazo por defecto
        return !deck.name.toLowerCase().includes('default') && !deck.is_default;
      });
      
      // Agregar el mazo por defecto real al principio
      const defaultDeck = response.data.find(deck => deck.is_default);
      if (defaultDeck) {
        filteredDecks.unshift(defaultDeck);
      }
    
      setDecks(filteredDecks || []);
      decksLoadedRef.current = true;
      
      // Si hay un mazo seleccionado pero no está en los mazos cargados, limpiar la selección
      if (selectedDeck?.deckId && !filteredDecks.some(d => String(d.id) === String(selectedDeck.deckId))) {
        console.log('Selected deck not found in loaded decks, clearing selection');
        if (teamNumber === 1) {
          setTeam1SelectedDecks(prev => prev.filter(d => d.playerId !== player.id));
        } else {
          setTeam2SelectedDecks(prev => prev.filter(d => d.playerId !== player.id));
        }
      }
    } catch (error) {
      console.warn(`Unable to load decks for user ${player.id}:`, error);
      setError('No se pudieron cargar los mazos');
      setDecks([]);
    }
    setLoading(false);
  }, [player?.id, teamNumber, selectedDeck?.deckId, setTeam1SelectedDecks, setTeam2SelectedDecks]);

  useEffect(() => {
    loadDecks();
  }, [loadDecks]);

  const handleDeckChange = useCallback((event) => {
    const deckId = event.target.value;
    console.log('Deck change event:', { deckId, availableDecks: decks });
    
    const selectedDeck = decks.find(d => String(d.id) === deckId);
    if (selectedDeck) {
      const newDeckData = {
        playerId: player.id,
        deckId: selectedDeck.id,
        deckName: selectedDeck.name,
        deckRaza: selectedDeck.raza
      };
      console.log('Saving new deck data:', newDeckData);

      if (teamNumber === 1) {
        setTeam1SelectedDecks(prev => {
          const filtered = prev.filter(d => d.playerId !== player.id);
          return [...filtered, newDeckData];
        });
      } else {
        setTeam2SelectedDecks(prev => {
          const filtered = prev.filter(d => d.playerId !== player.id);
          return [...filtered, newDeckData];
        });
      }
    }
  }, [decks, player.id, teamNumber, setTeam1SelectedDecks, setTeam2SelectedDecks]);

  // Asegurarnos de que el valor sea siempre un string y que exista en las opciones
  const selectValue = selectedDeck?.deckId && decks.some(d => String(d.id) === String(selectedDeck.deckId))
    ? String(selectedDeck.deckId)
    : '';

  return (
    <Box sx={{ mt: 1 }}>
      <Typography 
        variant="caption" 
        color="text.secondary"
        sx={{ 
          display: 'block',
          mb: 0.5,
          fontSize: '0.75rem',
          fontWeight: 500
        }}
      >
        Mazo
      </Typography>
      <FormControl 
        size="small" 
        fullWidth
        sx={{ 
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
            borderColor: selectedDeck ? 'primary.main' : 'divider',
            '&:hover': {
              borderColor: 'primary.main'
            }
          }
        }}
      >
        <Select
          value={selectValue}
          onChange={handleDeckChange}
          displayEmpty
          disabled={loading}
          sx={{
            '& .MuiSelect-select': { py: 1 },
            fontSize: '0.875rem',
            bgcolor: 'background.paper'
          }}
        >
          <MenuItem value="" disabled>
            {loading ? 'Cargando mazos...' : error || 'Seleccionar mazo'}
          </MenuItem>
          {decks.map((deck) => (
            <MenuItem 
              key={deck.id} 
              value={String(deck.id)}
              sx={{
                fontSize: '0.875rem',
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              {deck.is_default && (
                <Chip
                  label="Default"
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '0.7rem',
                    bgcolor: 'primary.main',
                    color: 'white',
                    '& .MuiChip-label': {
                      px: 0.5
                    }
                  }}
                />
              )}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                flex: 1
              }}>
                <Typography variant="body2">
                  {deck.name}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: '0.75rem',
                    lineHeight: 1
                  }}
                >
                  {deck.raza}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Solo re-renderizar si cambia el jugador, el equipo o el mazo seleccionado
  return prevProps.player?.id === nextProps.player?.id && 
         prevProps.teamNumber === nextProps.teamNumber &&
         prevProps.selectedDeck?.deckId === nextProps.selectedDeck?.deckId;
});

function NewMatchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  
  // Maintain the state activeInput in the main component
  const [activeInput, setActiveInput] = useState(1); // Initialize with 1 to activate the first input
  const [submitting, setSubmitting] = useState(false); // Add submission state

  const [matchMode, setMatchMode] = useState('1v1');
  const [tier, setTier] = useState('Amistoso');
  const [team1Players, setTeam1Players] = useState([]);
  const [team2Players, setTeam2Players] = useState([]);
  const [team1Decks, setTeam1Decks] = useState('');
  const [team2Decks, setTeam2Decks] = useState('');
  
  // States for debugging
  const [debugInfo, setDebugInfo] = useState({
    lastQuery: '',
    apiResponse: null,
    error: null,
    searchCount: 0
  });
  
  // States for search fields
  const [team1SearchInput, setTeam1SearchInput] = useState('');
  const [team2SearchInput, setTeam2SearchInput] = useState('');
  const [team1Options, setTeam1Options] = useState([]);
  const [team2Options, setTeam2Options] = useState([]);
  const [isSearching1, setIsSearching1] = useState(false);
  const [isSearching2, setIsSearching2] = useState(false);
  
  // States for controlling focus
  const [team1InputFocused, setTeam1InputFocused] = useState(false);
  const [team2InputFocused, setTeam2InputFocused] = useState(false);
  
  const [formError, setFormError] = useState('');
  
  // Add after other states
  const [numberOfMatches, setNumberOfMatches] = useState('1');
  const [team1Score, setTeam1Score] = useState(0);
  const [team2Score, setTeam2Score] = useState(0);
  const [error, setError] = useState(null);
  
  // Add a new state to control mobile input mode
  const [isMobileInputActive, setIsMobileInputActive] = useState(false);
  
  // Modify states for handling mazos
  const [team1SelectedDecks, setTeam1SelectedDecks] = useState([]); // [{playerId, deckId, deckName, deckRaza}]
  const [team2SelectedDecks, setTeam2SelectedDecks] = useState([]);
  const [highlights, setHighlights] = useState([]);
  
  // Add an object with highlight descriptions
  const highlightDescriptions = {
    'Remontada': 'Ganaste la partida luego de estar en clara desventaja.',
    'Paliza': 'Dominaste la partida desde el comienzo; el rival apenas pudo jugar.',
    'Combo': 'Ejecutaste una jugada clave o combinación letal que definió el juego.',
    'Resiliencia': 'Lograste mantenerte en pie contra presión constante hasta ganar.',
    'Sobreviviente': 'Ganaste con muy pocas cartas restantes en tu mazo, al borde de perder.',
    'Rapida': 'Victoria en muy pocos turnos; el duelo terminó enseguida.'
  };
  
  // Add function to validate the form
  const isFormValid = () => {
    // Validate that teams are complete according to mode
    if (matchMode === '1v1' && (team1Players.length !== 1 || team2Players.length !== 1)) {
      return false;
    }
    if (matchMode === '2v2' && (team1Players.length !== 2 || team2Players.length !== 2)) {
      return false;
    }

    // Validate that mazos are selected
    if (matchMode === '1v1' && (team1SelectedDecks.length !== 1 || team2SelectedDecks.length !== 1)) {
      return false;
    }
    if (matchMode === '2v2' && (team1SelectedDecks.length !== 2 || team2SelectedDecks.length !== 2)) {
      return false;
    }

    // Validate that there is at least one victory
    const totalScore = parseInt(team1Score) + parseInt(team2Score);
    if (totalScore < 1) {
      return false;
    }

    return true;
  };
  
  // Helper function to check if player is selected
  const isPlayerSelected = (userId) => {
    return team1Players.some(player => player.id === userId) || 
           team2Players.some(player => player.id === userId);
  };
  
  // Helper function to check if teams are complete
  const areTeamsComplete = () => {
    if (matchMode === '1v1') {
      return team1Players.length === 1 && team2Players.length === 1;
    } else { // 2v2
      return team1Players.length === 2 && team2Players.length === 2;
    }
  };
  
  // Separate search functions for each team
  const searchTeam1Users = async (query) => {
    console.log('4. searchTeam1Users Called:', {
      query,
      timestamp: new Date().getTime()
    });

    if (!query || query.length < 1) {
      console.log('5. Empty query, returning empty array');
      return [];
    }
    
    setIsSearching1(true);
    
    try {
      let endpoint;
      if (!isNaN(query) && query.trim() !== '') {
        endpoint = `/api/users/search?id=${query}`;
      } else if (query.startsWith('@')) {
        const usernameQuery = query.substring(1);
        if (usernameQuery.trim() !== '') {
          endpoint = `/api/users/search?username=${usernameQuery}`;
        } else {
          return [];
        }
      } else {
        endpoint = `/api/users/search?username=${query}`;
      }
      
      const response = await api.get(endpoint);
      setTeam1Options(response.data || []);
      setIsSearching1(false);
      return response.data || [];
    } catch (error) {
      console.error('Error in searchTeam1Users:', error);
      setIsSearching1(false);
      return [];
    }
  };

  const searchTeam2Users = async (query) => {
    console.log('4. searchTeam2Users Called:', {
      query,
      timestamp: new Date().getTime()
    });

    if (!query || query.length < 1) {
      console.log('5. Empty query, returning empty array');
      return [];
    }
    
    setIsSearching2(true);
    
    try {
      let endpoint;
      if (!isNaN(query) && query.trim() !== '') {
        endpoint = `/api/users/search?id=${query}`;
      } else if (query.startsWith('@')) {
        const usernameQuery = query.substring(1);
        if (usernameQuery.trim() !== '') {
          endpoint = `/api/users/search?username=${usernameQuery}`;
        } else {
          return [];
        }
      } else {
        endpoint = `/api/users/search?username=${query}`;
      }
      
      const response = await api.get(endpoint);
      setTeam2Options(response.data || []);
      setIsSearching2(false);
      return response.data || [];
    } catch (error) {
      console.error('Error in searchTeam2Users:', error);
      setIsSearching2(false);
      return [];
    }
  };

  // Create separate debounce functions
  const debouncedTeam1Search = useRef(
    debounce((query) => {
      console.log('3.5 Debounced team1 search executing:', {
        query,
        timestamp: new Date().getTime()
      });
      return searchTeam1Users(query);
    }, 300)
  ).current;

  const debouncedTeam2Search = useRef(
    debounce((query) => {
      console.log('3.5 Debounced team2 search executing:', {
        query,
        timestamp: new Date().getTime()
      });
      return searchTeam2Users(query);
    }, 300)
  ).current;

  // Separate search effects for each team
  useEffect(() => {
    if (team1SearchInput) {
      debouncedTeam1Search(team1SearchInput);
    } else {
      setTeam1Options([]);
    }
  }, [team1SearchInput]);

  useEffect(() => {
    if (team2SearchInput) {
      debouncedTeam2Search(team2SearchInput);
    } else {
      setTeam2Options([]);
    }
  }, [team2SearchInput]);

  useEffect(() => {
    if (matchMode === '1v1') {
      if (team1Players.length > 1) {
        setTeam1Players([team1Players[0]]);
      }
      if (team2Players.length > 1) {
        setTeam2Players([team2Players[0]]);
      }
    }
  }, [matchMode]);

  // Separate search handlers for each team
  const handleTeam1Search = useCallback((value) => {
    console.log('1. Team1 Input Change:', {
      value,
      timestamp: new Date().getTime()
    });
    setTeam1SearchInput(value);
  }, []);

  const handleTeam2Search = useCallback((value) => {
    console.log('1. Team2 Input Change:', {
      value,
      timestamp: new Date().getTime()
    });
    setTeam2SearchInput(value);
  }, []);

  // Function to get default deck of a user
  const fetchUserDefaultDeck = async (userId) => {
    try {
      const response = await api.get(`/api/decks/default/${userId}`, { 
        withCredentials: true 
      });
      return response.data;
    } catch (error) {
      console.warn(`No default deck found for user ${userId}`);
      return null;
    }
  };

  // Update function to get all decks of a user
  const fetchUserDecks = async (userId) => {
    try {
      const response = await api.get(`/api/users/${userId}/decks`, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching user decks:', error);
      return [];
    }
  };

  // Modify handleTeam1Select to ensure deck is saved correctly
  const handleTeam1Select = useCallback(async (option) => {
    if (isPlayerSelected(option.id)) {
      setError('Este jugador ya está seleccionado en un equipo');
      return;
    }

    if (matchMode === '1v1' && team1Players.length >= 1) {
      setError('En modalidad 1v1, solo se permite 1 jugador por equipo');
      return;
    }
    
    if (matchMode === '2v2' && team1Players.length >= 2) {
      setError('En modalidad 2v2, solo se permiten 2 jugadores por equipo');
      return;
    }
    
    // Get default deck of the player
    const defaultDeck = await fetchUserDefaultDeck(option.id);
    console.log('Default deck for player 1:', defaultDeck);
    
    if (defaultDeck) {
      const newDeckData = {
        playerId: option.id,
        deckId: defaultDeck.id,
        deckName: defaultDeck.name,
        deckRaza: defaultDeck.raza
      };
      console.log('Saving deck for player 1:', newDeckData);
      setTeam1SelectedDecks(prev => {
        const filtered = prev.filter(d => d.playerId !== option.id);
        return [...filtered, newDeckData];
      });
    } else {
      console.error('No default deck found for player:', option.id);
      setError('El jugador no tiene un mazo por defecto configurado');
      return;
    }
    
    setTeam1Players([...team1Players, option]);
    setTeam1SearchInput('');
    setTeam1Options([]);
    setError(null);

    const willBeComplete = matchMode === '1v1' ? 
      team1Players.length + 1 >= 1 : 
      team1Players.length + 1 >= 2;
    
    if (willBeComplete) {
      setActiveInput(2);
    }
  }, [matchMode, team1Players, isPlayerSelected]);

  // Modify handleTeam2Select similarly
  const handleTeam2Select = useCallback(async (option) => {
    if (isPlayerSelected(option.id)) {
      setError('Este jugador ya está seleccionado en un equipo');
      return;
    }

    if (matchMode === '1v1' && team2Players.length >= 1) {
      setError('En modalidad 1v1, solo se permite 1 jugador por equipo');
      return;
    }
    
    if (matchMode === '2v2' && team2Players.length >= 2) {
      setError('En modalidad 2v2, solo se permiten 2 jugadores por equipo');
      return;
    }
    
    // Get default deck of the player
    const defaultDeck = await fetchUserDefaultDeck(option.id);
    console.log('Default deck for player 2:', defaultDeck);
    
    if (defaultDeck) {
      const newDeckData = {
        playerId: option.id,
        deckId: defaultDeck.id,
        deckName: defaultDeck.name,
        deckRaza: defaultDeck.raza
      };
      console.log('Saving deck for player 2:', newDeckData);
      setTeam2SelectedDecks(prev => {
        const filtered = prev.filter(d => d.playerId !== option.id);
        return [...filtered, newDeckData];
      });
    } else {
      console.error('No default deck found for player:', option.id);
      setError('El jugador no tiene un mazo por defecto configurado');
      return;
    }
    
    setTeam2Players([...team2Players, option]);
    setTeam2SearchInput('');
    setTeam2Options([]);
    setError(null);

    const willBeComplete = matchMode === '1v1' ? 
      team2Players.length + 1 >= 1 : 
      team2Players.length + 1 >= 2;
    
    if (willBeComplete) {
      setActiveInput(null);
    }
  }, [matchMode, team2Players, isPlayerSelected]);

  // Remove a selected user
  const handleRemoveUser = (userId, teamNumber) => {
    if (teamNumber === 1) {
      setTeam1Players(team1Players.filter(player => player.id !== userId));
      setTeam1SelectedDecks(team1SelectedDecks.filter(deck => deck.playerId !== userId));
      setActiveInput(1);
    } else {
      setTeam2Players(team2Players.filter(player => player.id !== userId));
      setTeam2SelectedDecks(team2SelectedDecks.filter(deck => deck.playerId !== userId));
      setActiveInput(2);
    }
  };

  // Handle input in search field
  const handleInputChange = (e, teamNumber) => {
    const value = e.target.value;
    console.log('1. Input Change Event:', {
      value,
      teamNumber: teamNumber,
      timestamp: new Date().getTime()
    });
    
    if (teamNumber === 1) {
      setTeam1SearchInput(value);
    } else {
      setTeam2SearchInput(value);
    }
  };

  // Function to change deck of a player
  const handleDeckChange = async (teamNumber, playerId) => {
    const decks = await fetchUserDecks(playerId);
    // Here you might open a dialog to select the deck
    // For now I'll just show how to update the state
    if (decks.length > 0) {
      const newDeck = decks[0]; // In reality this would come from the dialog
      if (teamNumber === 1) {
        setTeam1SelectedDecks(prev => prev.map(deck => 
          deck.playerId === playerId ? {
            ...deck,
            deckId: newDeck.id,
            deckName: newDeck.name,
            deckRaza: newDeck.raza
          } : deck
        ));
      } else {
        // Similar for team 2
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Logs initial state for verification
    console.log('Initial state:', {
      matchMode,
      team1Players,
      team2Players,
      team1SelectedDecks,
      team2SelectedDecks,
      team1Score,
      team2Score
    });

    // Specific validations with clear messages
    if (matchMode === '1v1' && (team1Players.length !== 1 || team2Players.length !== 1)) {
      setError('Debes seleccionar exactamente 1 jugador para cada equipo en modo 1v1');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (matchMode === '2v2' && (team1Players.length !== 2 || team2Players.length !== 2)) {
      setError('Debes seleccionar exactamente 2 jugadores para cada equipo en modo 2v2');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate that each player has a deck assigned
    const team1MissingDecks = team1Players.filter(player => 
      !team1SelectedDecks.some(deck => deck.playerId === player.id)
    );
    const team2MissingDecks = team2Players.filter(player => 
      !team2SelectedDecks.some(deck => deck.playerId === player.id)
    );

    if (team1MissingDecks.length > 0 || team2MissingDecks.length > 0) {
      const missingPlayers = [
        ...team1MissingDecks.map(p => p.username),
        ...team2MissingDecks.map(p => p.username)
      ];
      setError(`Los siguientes jugadores no tienen mazo asignado: ${missingPlayers.join(', ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate that there is at least one victory
    const totalScore = parseInt(team1Score) + parseInt(team2Score);
    if (totalScore < 1) {
      setError('Debe haber al menos una victoria para guardar la partida');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Prepare team data
      const team1Data = {
        players: team1Players.map(player => ({
          user_id: player.id,
          approved: false
        })),
        decks: team1SelectedDecks.map(deck => ({
          deck_id: deck.deckId,
          deck_name: deck.deckName,
          deck_raza: deck.deckRaza
        }))
      };

      const team2Data = {
        players: team2Players.map(player => ({
          user_id: player.id,
          approved: false
        })),
        decks: team2SelectedDecks.map(deck => ({
          deck_id: deck.deckId,
          deck_name: deck.deckName,
          deck_raza: deck.deckRaza
        }))
      };

      // Simplified logs for debugging
      console.log('Sending data to server:', {
        matchMode,
        tier,
        team1Players: team1Players.length,
        team2Players: team2Players.length,
        team1Decks: team1SelectedDecks.length,
        team2Decks: team2SelectedDecks.length,
        highlights: highlights.length
      });

      const matchData = {
        matchMode,
        tier,
        team1Players: team1Players.map(p => p.id),
        team2Players: team2Players.map(p => p.id),
        team1Decks: team1SelectedDecks.map(deck => ({
          id: deck.deckId,
          name: deck.deckName,
          raza: deck.deckRaza,
          userId: deck.playerId
        })),
        team2Decks: team2SelectedDecks.map(deck => ({
          id: deck.deckId,
          name: deck.deckName,
          raza: deck.deckRaza,
          userId: deck.playerId
        })),
        highlights: highlights.map(highlight => ({
          type: highlight,
          description: highlight
        })),
        team1Score: parseInt(team1Score),
        team2Score: parseInt(team2Score)
      };

      // Create the match
      const response = await api.post('/api/matches', matchData, {
        timeout: 30000 // Increase timeout to 30 seconds for match creation
      });

      if (response.data) {
        navigate('/my-matches', { state: { selectedTab: 0 } });
      }
    } catch (error) {
      console.error('Complete error:', error);
      console.error('Server response:', error.response?.data);
      console.error('Error details:', error.response?.data?.details);
      // Scroll to error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Handle different types of errors
      if (error.response?.status === 400) {
        // If there's a specific server message, use it
        const errorMessage = error.response.data?.message || error.response.data?.error || 'Error al crear la partida. Por favor verifica los datos ingresados.';
        
        // If there are specific validation errors, show them
        if (error.response.data?.errors) {
          const validationErrors = Object.entries(error.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('\n');
          setError(`${errorMessage}\n${validationErrors}`);
        } else if (error.response.data?.details) {
          // Show specific error details
          const details = Object.entries(error.response.data.details)
            .map(([field, message]) => `${field}: ${message}`)
            .join('\n');
          setError(`${errorMessage}\n${details}`);
        } else {
          setError(errorMessage);
        }
      } else if (error.response?.status === 401) {
        setError('Tu sesión ha expirado. Por favor inicia sesión nuevamente.');
      } else if (error.response?.status === 403) {
        setError('No tienes permisos para crear esta partida.');
      } else {
        setError('Error al crear la partida. Por favor intenta nuevamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Add function to get highlight icon
  const getHighlightIcon = (highlight) => {
    switch (highlight) {
      case 'Remontada':
        return <RemontadaIcon sx={{ fontSize: '1.2rem' }} />;
      case 'Paliza':
        return <PalizaIcon sx={{ fontSize: '1.2rem' }} />;
      case 'Combo':
        return <ComboIcon sx={{ fontSize: '1.2rem' }} />;
      case 'Resiliencia':
        return <ResilienciaIcon sx={{ fontSize: '1.2rem' }} />;
      case 'Sobreviviente':
        return <SobrevivienteIcon sx={{ fontSize: '1.2rem' }} />;
      case 'Rapida':
        return <RapidaIcon sx={{ fontSize: '1.2rem' }} />;
      default:
        return null;
    }
  };

  // Effect to handle preloaded match data from navigation
  useEffect(() => {
    if (location.state?.matchData && user) {
      const { matchData } = location.state;
      console.log('🔄 Inicializando datos desde partida rechazada:', matchData);

      // Set match mode and tier
      setMatchMode(matchData.matchMode || '1v1');
      setTier(matchData.tier || 'Amistoso');

      // Set number of matches and scores
      setNumberOfMatches(String(matchData.totalMatches || 1));
      setTeam1Score(matchData.team1?.score || 0);
      setTeam2Score(matchData.team2?.score || 0);

      // Set players
      if (matchData.team1?.players) {
        setTeam1Players(matchData.team1.players.map(p => ({
          id: p.id,
          username: p.username
        })));
      }
      if (matchData.team2?.players) {
        setTeam2Players(matchData.team2.players.map(p => ({
          id: p.id,
          username: p.username
        })));
      }

      // Set decks
      if (matchData.team1?.decks) {
        setTeam1SelectedDecks(matchData.team1.decks.map((deck, index) => ({
          playerId: matchData.team1.players[index]?.id,
          deckId: deck.deck_id,
          deckName: deck.deck_name || 'Mazo sin nombre',
          deckRaza: deck.deck_raza || 'Desconocida'
        })));
      }
      if (matchData.team2?.decks) {
        setTeam2SelectedDecks(matchData.team2.decks.map((deck, index) => ({
          playerId: matchData.team2.players[index]?.id,
          deckId: deck.deck_id,
          deckName: deck.deck_name || 'Mazo sin nombre',
          deckRaza: deck.deck_raza || 'Desconocida'
        })));
      }

      // Set highlights
      if (matchData.highlights) {
        console.log('🎯 NewMatchPage - Procesando highlights:', matchData.highlights);
        const processedHighlights = matchData.highlights
          .map(h => {
            // Si es un string y es uno de los highlights válidos, usarlo directamente
            if (typeof h === 'string' && ['Remontada', 'Paliza', 'Combo', 'Resiliencia', 'Sobreviviente', 'Rapida'].includes(h)) {
              return h;
            }
            // Si es un objeto, intentar obtener el tipo o descripción
            if (typeof h === 'object' && h !== null) {
              const highlightText = h.type || h.description;
              if (typeof highlightText === 'string' && ['Remontada', 'Paliza', 'Combo', 'Resiliencia', 'Sobreviviente', 'Rapida'].includes(highlightText)) {
                return highlightText;
              }
            }
            return null;
          })
          .filter(h => h !== null);

        console.log('✨ NewMatchPage - Highlights procesados:', processedHighlights);
        setHighlights(processedHighlights);
      } else {
        console.log('❌ NewMatchPage - No se encontraron highlights en los datos');
        setHighlights([]);
      }

      console.log('✅ Datos inicializados correctamente');
    }
  }, [location.state, user]);

  return (
    <>
      <Box sx={{ 
        p: { xs: 1, sm: 2 },
        mt: '0px',
        mb: '56px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '800px',
        mx: 'auto',
        position: 'relative'
      }}>
        <Stack spacing={2} sx={{ width: '100%' }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}>
            <Typography 
              variant="h5" 
              fontWeight="bold"
              color="secondary"
            >
              Nueva Partida
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {/* Match Configuration Card */}
              <SectionTitle title="Configuración">
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    flexWrap: 'wrap',
                    alignItems: 'flex-start'
                  }}>
                    {/* Mode Selection */}
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      flex: { xs: '1', sm: '0 1 200px' }
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        Modo
                      </Typography>
                      <FormControl>
                        <Select
                          value={matchMode}
                          onChange={(e) => setMatchMode(e.target.value)}
                          size="small"
                          sx={{
                            minWidth: '120px',
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }
                          }}
                        >
                          <MenuItem value="1v1">
                            <GameIcon fontSize="small" sx={{ color: 'primary.main' }} />
                            1v1
                          </MenuItem>
                          <MenuItem value="2v2" disabled>
                            <GameIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            2v2
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                  {/* Tier Selection */}
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      flex: { xs: '1', sm: '0 1 200px' }
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        Tipo
                      </Typography>
                      <FormControl>
                        <Select
                          value={tier}
                          onChange={(e) => setTier(e.target.value)}
                          size="small"
                          sx={{
                            minWidth: '120px',
                            '& .MuiSelect-select': {
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }
                          }}
                        >
                          <MenuItem value="Amistoso">
                              <TrophyIcon fontSize="small" sx={{ color: 'primary.main' }} />
                            Amistoso
                            </MenuItem>
                          <MenuItem value="Amistoso c/ Arbitro" disabled>
                            <TrophyIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            Amistoso c/ Arbitro
                          </MenuItem>
                          <MenuItem value="Arena" disabled>
                            <TrophyIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            Arena
                          </MenuItem>
                          <MenuItem value="Torneo" disabled>
                            <TrophyIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            Torneo
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
              </SectionTitle>

              {/* Teams Card */}
              <SectionTitle title="Equipos">
                  {/* Team 1 */}
                  <Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 0,
                      width: '100%'
                    }}>
                      <Box sx={{ flex: 1, pr: '100px' }}>
                        <Typography 
                          variant="subtitle1" 
                          color="text.secondary" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '1rem'
                          }}
                        >
                          Equipo 1
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100px', textAlign: 'left' }}>
                        <Typography 
                          variant="subtitle2" 
                          color="text.secondary" 
                          sx={{ mb: 0.5 }}
                        >
                          Victorias
                        </Typography>
                      </Box>
                    </Box>
                    <Stack 
                      direction="row" 
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box sx={{ flex: 1 }}>
                      <CustomPlayerInput
                        teamNumber={1}
                        players={team1Players}
                        options={team1Options}
                        searchValue={team1SearchInput}
                        handleSearch={handleTeam1Search}
                        handleSelectOption={handleTeam1Select}
                        selectedDecks={team1SelectedDecks}
                        matchMode={matchMode}
                        activeInput={activeInput}
                        handleRemoveUser={handleRemoveUser}
                        setTeam1SelectedDecks={setTeam1SelectedDecks}
                        setTeam2SelectedDecks={setTeam2SelectedDecks}
                        setActiveInput={setActiveInput}
                        isSearching={isSearching1}
                        showAnyDeckOption={false}
                      />
                      </Box>
                      <NumberInput
                        value={team1Score}
                        onChange={setTeam1Score}
                        width="100px"
                      />
                    </Stack>
                  </Box>

                  <Divider sx={{ 
                    my: 2,
                    borderColor: '#c89e38',
                    opacity: 0.65,
                    borderWidth: '1px'
                  }} />

                  {/* Team 2 */}
                  <Box>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 0,
                      width: '100%'
                    }}>
                      <Box sx={{ flex: 1, pr: '100px' }}>
                        <Typography 
                          variant="subtitle1" 
                          color="text.secondary" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '1rem'
                          }}
                        >
                          Equipo 2
                        </Typography>
                      </Box>
                      <Box sx={{ width: '100px', textAlign: 'left' }}>
                        <Typography 
                          variant="subtitle2" 
                          color="text.secondary" 
                          sx={{ mb: 0.5 }}
                        >
                          Victorias
                        </Typography>
                      </Box>
                    </Box>
                    <Stack 
                      direction="row" 
                      spacing={1.5}
                      alignItems="flex-start"
                    >
                      <Box sx={{ flex: 1 }}>
                      <CustomPlayerInput
                        teamNumber={2}
                        players={team2Players}
                        options={team2Options}
                        searchValue={team2SearchInput}
                        handleSearch={handleTeam2Search}
                        handleSelectOption={handleTeam2Select}
                        selectedDecks={team2SelectedDecks}
                        matchMode={matchMode}
                        activeInput={activeInput}
                        handleRemoveUser={handleRemoveUser}
                        setTeam1SelectedDecks={setTeam1SelectedDecks}
                        setTeam2SelectedDecks={setTeam2SelectedDecks}
                        setActiveInput={setActiveInput}
                        isSearching={isSearching2}
                        showAnyDeckOption={false}
                      />
                      </Box>
                      <NumberInput
                        value={team2Score}
                        onChange={setTeam2Score}
                        width="100px"
                      />
                    </Stack>
                  </Box>

                  <Divider sx={{ 
                    my: 2,
                    borderColor: '#c89e38',
                    opacity: 0.65,
                    borderWidth: '1px'
                  }} />

                  {/* Score Summary */}
                  <Box>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: 'text.primary',
                        mb: 1
                      }}
                    >
                      Resumen
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      py: 2,
                      px: 3,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <Stack 
                        direction="row" 
                        spacing={2} 
                        alignItems="center"
                        sx={{ width: '100%', maxWidth: '600px' }}
                      >
                        {/* Team 1 */}
                        <Stack 
                          direction="row" 
                          spacing={1} 
                          alignItems="center"
                          sx={{ flex: 1 }}
                        >
                          <AvatarGroup 
                            max={2}
                            sx={{
                              '& .MuiAvatar-root': { 
                                width: 32,
                                height: 32,
                                fontSize: '0.875rem',
                                border: '2px solid',
                                borderColor: parseInt(team1Score) > parseInt(team2Score) ? 'primary.main' : 'grey.500',
                                marginLeft: '-8px',
                                '&:first-of-type': {
                                  marginLeft: 0
                                }
                              }
                            }}
                          >
                            {team1Players.map((player, idx) => (
                              <Avatar 
                                key={idx}
                                sx={{ 
                                  bgcolor: parseInt(team1Score) > parseInt(team2Score) ? 'primary.main' : 'grey.700',
                                  color: 'white'
                                }}
                              >
                                {player.username[0]?.toUpperCase()}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 'medium',
                              color: 'text.primary',
                              ml: 1
                            }}
                          >
                            {team1Players.map(p => p.username).join(' & ')}
                          </Typography>
                        </Stack>

                        {/* Score */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: 1
                        }}>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: 'bold',
                              minWidth: '40px',
                              textAlign: 'center',
                              color: parseInt(team1Score) > parseInt(team2Score) ? 'primary.main' : 'text.secondary'
                            }}
                          >
                            {team1Score}
                          </Typography>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: 'text.secondary'
                            }}
                          >
                            VS
                          </Typography>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: 'bold',
                              minWidth: '40px',
                              textAlign: 'center',
                              color: parseInt(team2Score) > parseInt(team1Score) ? 'primary.main' : 'text.secondary'
                            }}
                          >
                            {team2Score}
                          </Typography>
                        </Box>

                        {/* Team 2 */}
                        <Stack 
                          direction="row" 
                          spacing={1} 
                          alignItems="center"
                          sx={{ flex: 1, justifyContent: 'flex-end' }}
                        >
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 'medium',
                              color: 'text.primary',
                              mr: 1
                            }}
                          >
                            {team2Players.map(p => p.username).join(' & ')}
                          </Typography>
                          <AvatarGroup 
                            max={2}
                            sx={{
                              '& .MuiAvatar-root': { 
                                width: 32,
                                height: 32,
                                fontSize: '0.875rem',
                                border: '2px solid',
                                borderColor: parseInt(team2Score) > parseInt(team1Score) ? 'primary.main' : 'grey.500',
                                marginRight: '-8px',
                                '&:last-of-type': {
                                  marginRight: 0
                                }
                              }
                            }}
                          >
                            {team2Players.map((player, idx) => (
                              <Avatar 
                                key={idx}
                                sx={{ 
                                  bgcolor: parseInt(team2Score) > parseInt(team1Score) ? 'primary.main' : 'grey.700',
                                  color: 'white'
                                }}
                              >
                                {player.username[0]?.toUpperCase()}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                        </Stack>
                      </Stack>
                    </Box>
                  </Box>
              </SectionTitle>

              {/* Highlights Card */}
              <SectionTitle title="Highlights" subtitle="(Max 3)">
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    {['Remontada', 'Paliza', 'Combo', 'Resiliencia', 'Sobreviviente', 'Rapida'].map((highlight) => (
                      <Tooltip
                        key={highlight}
                        title={
                          <Typography>
                            <strong>{highlight}:</strong> {highlightDescriptions[highlight]}
                          </Typography>
                        }
                        placement="top"
                        arrow
                        enterDelay={200}
                        leaveDelay={0}
                        sx={{
                          '& .MuiTooltip-tooltip': {
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1,
                            fontSize: '0.875rem',
                            maxWidth: 300,
                            textAlign: 'center'
                          }
                        }}
                      >
                        <Chip
                        icon={getHighlightIcon(highlight)}
                        label={highlight}
                        onClick={() => {
                          setHighlights(prev => {
                            if (prev.includes(highlight)) {
                              return prev.filter(h => h !== highlight);
                            }
                            if (prev.length >= 3) {
                              return prev;
                            }
                            return [...prev, highlight];
                          });
                        }}
                        color={highlights.includes(highlight) ? 'primary' : 'default'}
                        sx={{
                          borderRadius: 1,
                          '&.MuiChip-root': {
                            height: '32px',
                            '& .MuiChip-label': {
                              fontSize: '0.875rem'
                            },
                            '& .MuiChip-icon': {
                              color: highlights.includes(highlight) ? 'white' : 'inherit'
                            }
                          }
                        }}
                      />
                      </Tooltip>
                    ))}
                  </Box>
              </SectionTitle>
            </Stack>

            {/* Save Button */}
            <Box sx={{ 
              mt: 3,
              width: '100%',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={submitting || !isFormValid()}
                sx={{
                  py: 1.3,
                  px: 4,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  boxShadow: 'none',
                  minWidth: '200px',
                  bgcolor: 'primary.main',
                  '&:hover': {
                    boxShadow: 'none',
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                {submitting ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Guardar ${parseInt(team1Score) + parseInt(team2Score)} Partida${parseInt(team1Score) + parseInt(team2Score) !== 1 ? 's' : ''}`
                )}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Bottom Navigation */}
      <BottomNav newMatchButtonSize={0.4} />
    </>
  );
}

export default NewMatchPage;